import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { X, Dumbbell, Zap, Target, Sparkles } from "lucide-react";
import MuscleDiagram from "./MuscleDiagram";
import { exerciseLibrary } from "../data/exerciseLibrary";
import { haptic } from "../lib/haptics";

/**
 * Bottom-sheet modal that explains a single exercise.
 *
 * Triggered from the (i) button next to the exercise name inside a workout
 * session. Looks the exercise up in the curated library by a normalised name
 * match so the AI-generated workout (which only includes `name + sets + reps`)
 * can still surface full form cues + a pro tip.
 *
 * Portaled to <body> via React.createPortal because the parent `<MobileCarousel>`
 * uses CSS `transform: translateX`, which would otherwise re-parent any
 * `position: fixed` child to the carousel cell and break full-screen overlays.
 */

/** Lookup helper — normalises whitespace + casing, also matches the library
 *  entry as a substring of the AI's name (or vice-versa), e.g. "Romanian
 *  Deadlift (RDL)" against "Romanian Deadlift". */
const normalise = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const findLibraryEntry = (name) => {
  if (!name) return null;
  const n = normalise(name);
  const exact = exerciseLibrary.find((e) => normalise(e.name) === n);
  if (exact) return exact;
  // Loose containment match — picks up "Bench Press (Pause)" → "Bench Press" etc.
  return (
    exerciseLibrary.find((e) => {
      const en = normalise(e.name);
      return en.length >= 4 && (n.includes(en) || en.includes(n));
    }) || null
  );
};

const MetaPill = ({ icon: Icon, label }) => (
  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/70 border border-white/10 px-2.5 py-1 rounded-full">
    <Icon className="w-3 h-3 text-[#6B9BFF]" />
    {label}
  </span>
);

export default function ExerciseInfoModal({ open, exercise, onClose }) {
  const lib = exercise ? findLibraryEntry(exercise.name) : null;

  // Merge: AI-generated values win for prescription (sets/reps/rest); the
  // library wins for didactic content (steps, tip, equipment, difficulty).
  const steps = lib?.steps || (exercise?.notes ? [exercise.notes] : []);
  const tip = lib?.tip;
  const equipment = lib?.equipment || exercise?.equipment;
  const difficulty = lib?.difficulty;
  const muscleGroup = exercise?.muscle_group || lib?.muscle_group;

  // Lock body scroll while open (helps the workout carousel under it stay
  // put even when the user drags inside the sheet).
  useEffect(() => {
    if (!open) return undefined;
    haptic.tap();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open || !exercise) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
      data-testid="exercise-info-modal"
      role="dialog"
      aria-modal="true"
      aria-label={`How to perform ${exercise.name}`}
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm fade-up"
        style={{ animationDuration: "0.25s" }}
        onClick={onClose}
        data-testid="exercise-info-backdrop"
      />

      {/* Sheet (mobile: from bottom; desktop: centered) — follows the
          user's selected theme (no dark-island pin), so the modal reads
          natively whether they're in light or dark mode. */}
      <div
        className="relative w-full sm:w-[520px] sm:max-w-[92vw] max-h-[88vh] bg-[#121212] border-t border-white/10 sm:border sm:rounded-2xl rounded-t-3xl flex flex-col"
        style={{
          animation: "sheetUp 0.32s cubic-bezier(0.2, 0.9, 0.2, 1) both",
        }}
      >
        {/* Drag handle (mobile) */}
        <div className="sm:hidden flex justify-center pt-3 pb-1">
          <span className="w-10 h-1.5 rounded-full bg-white/15" />
        </div>

        {/* Header */}
        <div className="flex items-start gap-3 px-5 pt-3 pb-4 border-b border-white/5">
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow text-[#6B9BFF] mb-1">How to perform</div>
            <h2 className="font-display text-xl sm:text-2xl text-white leading-tight">
              {exercise.name}
            </h2>
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
              {muscleGroup && <MetaPill icon={Target} label={muscleGroup.replace(/_/g, " ")} />}
              {equipment && <MetaPill icon={Dumbbell} label={equipment} />}
              {difficulty && <MetaPill icon={Zap} label={difficulty} />}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 w-9 h-9 inline-flex items-center justify-center border border-white/10 hover:border-white/30 text-white/70 hover:text-white rounded-full transition-colors"
            data-testid="exercise-info-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-5 py-5 space-y-6 flex-1">
          {/* Muscle diagram */}
          {muscleGroup && (
            <div className="flex flex-col items-center" data-testid="exercise-info-diagram">
              <MuscleDiagram group={muscleGroup} size={140} />
              <div className="text-[10px] uppercase tracking-widest text-white/40 mt-2">
                {muscleGroup.replace(/_/g, " ")}
              </div>
            </div>
          )}

          {/* Steps */}
          {steps.length > 0 ? (
            <div data-testid="exercise-info-steps">
              <div className="label-eyebrow text-white/50 mb-3">Form cues</div>
              <ol className="space-y-3">
                {steps.map((s, i) => (
                  // Composite key — steps change per-exercise; index alone would
                  // reuse stale list-item nodes across opens. Including the
                  // string content keeps React's reconciler honest.
                  <li key={`${i}-${(s || "").slice(0, 32)}`} className="flex gap-3 text-sm text-white/80 leading-relaxed">
                    <span className="shrink-0 w-6 h-6 rounded-full border border-[#6B9BFF]/40 text-[#6B9BFF] inline-flex items-center justify-center font-display text-xs">
                      {i + 1}
                    </span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          ) : (
            <div className="text-sm text-white/55 italic">
              No detailed form cues are available for this variation.
              Refer to a qualified coach if you&apos;re new to the movement.
            </div>
          )}

          {/* Tip */}
          {tip && (
            <div
              className="bg-[#6B9BFF]/[0.07] border border-[#6B9BFF]/25 rounded-xl p-4 flex gap-3"
              data-testid="exercise-info-tip"
            >
              <Sparkles className="w-4 h-4 text-[#6B9BFF] shrink-0 mt-0.5" />
              <div>
                <div className="label-eyebrow text-[#6B9BFF] mb-1">Pro tip</div>
                <p className="text-sm text-white/80 leading-relaxed">{tip}</p>
              </div>
            </div>
          )}

          {/* AI-generated notes (if distinct from the library steps) */}
          {exercise.notes && !steps.includes(exercise.notes) && (
            <div className="border-t border-white/5 pt-4">
              <div className="label-eyebrow text-white/50 mb-2">From your coach</div>
              <p className="text-sm text-white/80 leading-relaxed italic">{exercise.notes}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/5">
          <button
            type="button"
            onClick={onClose}
            style={{ backgroundColor: "var(--accent)", color: "var(--accent-fg)" }}
            className="w-full font-bold uppercase tracking-widest text-xs py-3 rounded-full hover:opacity-90 transition-opacity"
            data-testid="exercise-info-got-it"
          >
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
